import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import LoadingSpinner from '../components/LoadingSpinner';

interface NotificationPrefs {
  reminderMinutes: number;
  notifyOnBooking: boolean;
  notifyOnCancellation: boolean;
  notifyOnLinkAccess: boolean;
  dailySummary: boolean;
  dailySummaryTime: string;
}

export default function Settings() {
  const { user, refreshUser } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Profile form
  const [displayName, setDisplayName] = useState('');
  const [timezone, setTimezone] = useState('');
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);

  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  // Notification prefs
  const [prefs, setPrefs] = useState<NotificationPrefs | null>(null);
  const [isUpdatingPrefs, setIsUpdatingPrefs] = useState(false);

  // Calendar export
  const [calendarInfo, setCalendarInfo] = useState<{
    hasSubscription: boolean;
    subscriptionUrl?: string;
    googleCalendarUrl?: string;
    appleCalendarUrl?: string;
  } | null>(null);
  const [isGeneratingCalendar, setIsGeneratingCalendar] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Set profile data from user
        if (user) {
          setDisplayName(user.displayName);
          setTimezone(user.timezone);
        }

        // Fetch notification preferences
        const prefsResponse = await api.get('/notifications/preferences');
        setPrefs(prefsResponse.data);

        // Fetch calendar subscription info
        try {
          const calendarResponse = await api.get('/calendar/subscription/info');
          setCalendarInfo(calendarResponse.data);
        } catch {
          // Calendar feature may not be set up yet
          setCalendarInfo({ hasSubscription: false });
        }
      } catch {
        setError('Failed to load settings');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsUpdatingProfile(true);
    setError('');
    setSuccess('');

    try {
      await api.put('/users/profile', { displayName, timezone });
      await refreshUser();
      setSuccess('Profile updated successfully');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to update profile');
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handlePasswordUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }

    setIsUpdatingPassword(true);

    try {
      await api.post('/auth/change-password', { currentPassword, newPassword });
      setSuccess('Password changed successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || 'Failed to change password');
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handlePrefsUpdate = async (updates: Partial<NotificationPrefs>) => {
    if (!prefs) return;

    setIsUpdatingPrefs(true);
    setError('');

    try {
      const response = await api.put('/notifications/preferences', updates);
      setPrefs(response.data.preferences);
    } catch {
      setError('Failed to update notification preferences');
    } finally {
      setIsUpdatingPrefs(false);
    }
  };

  const handleGenerateCalendarToken = async () => {
    setIsGeneratingCalendar(true);
    setError('');

    try {
      const response = await api.post('/calendar/generate-token');
      setCalendarInfo({
        hasSubscription: true,
        subscriptionUrl: response.data.subscriptionUrl,
        googleCalendarUrl: response.data.googleCalendarUrl,
        appleCalendarUrl: `webcal://${response.data.subscriptionUrl.replace(/^https?:\/\//, '')}`
      });
      setSuccess('Calendar subscription created');
    } catch {
      setError('Failed to generate calendar subscription');
    } finally {
      setIsGeneratingCalendar(false);
    }
  };

  const handleDownloadCalendar = async () => {
    try {
      const response = await api.get('/calendar/download/my-schedule', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'darts-schedule.ics');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch {
      setError('Failed to download calendar');
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="page-title">Settings</h1>

      {error && (
        <div className="bg-red-900/50 text-red-300 px-4 py-3 rounded-lg text-sm border border-red-800">
          {error}
          <button onClick={() => setError('')} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-900/50 text-green-300 px-4 py-3 rounded-lg text-sm border border-green-800">
          {success}
          <button onClick={() => setSuccess('')} className="ml-2 underline">
            Dismiss
          </button>
        </div>
      )}

      {/* Profile */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-4">Profile</h2>
        <form onSubmit={handleProfileUpdate} className="space-y-4">
          <div>
            <label className="label">Email</label>
            <input
              type="email"
              value={user?.email || ''}
              className="input bg-[#252550] cursor-not-allowed"
              disabled
            />
          </div>

          <div>
            <label className="label">Display Name</label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="input"
              required
              maxLength={100}
            />
          </div>

          <div>
            <label className="label">Timezone</label>
            <select
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
              className="input"
            >
              <option value="Europe/London">Europe/London (GMT/BST)</option>
              <option value="America/New_York">America/New York (EST/EDT)</option>
              <option value="America/Los_Angeles">America/Los Angeles (PST/PDT)</option>
              <option value="America/Chicago">America/Chicago (CST/CDT)</option>
              <option value="Europe/Paris">Europe/Paris (CET/CEST)</option>
              <option value="Europe/Berlin">Europe/Berlin (CET/CEST)</option>
              <option value="Asia/Tokyo">Asia/Tokyo (JST)</option>
              <option value="Australia/Sydney">Australia/Sydney (AEST/AEDT)</option>
            </select>
          </div>

          <button type="submit" disabled={isUpdatingProfile} className="btn-primary">
            {isUpdatingProfile ? 'Saving...' : 'Save Profile'}
          </button>
        </form>
      </div>

      {/* Password */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-4">Change Password</h2>
        <form onSubmit={handlePasswordUpdate} className="space-y-4">
          <div>
            <label className="label">Current Password</label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className="input"
              required
            />
          </div>

          <div>
            <label className="label">New Password</label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className="input"
              required
              minLength={8}
            />
          </div>

          <div>
            <label className="label">Confirm New Password</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="input"
              required
            />
          </div>

          <button type="submit" disabled={isUpdatingPassword} className="btn-primary">
            {isUpdatingPassword ? 'Changing...' : 'Change Password'}
          </button>
        </form>
      </div>

      {/* Notifications */}
      {prefs && (
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">Notifications</h2>
          <div className="space-y-4">
            <div>
              <label className="label">Game Reminder</label>
              <select
                value={prefs.reminderMinutes}
                onChange={(e) => handlePrefsUpdate({ reminderMinutes: parseInt(e.target.value) })}
                className="input"
                disabled={isUpdatingPrefs}
              >
                <option value={5}>5 minutes before</option>
                <option value={10}>10 minutes before</option>
                <option value={15}>15 minutes before</option>
                <option value={30}>30 minutes before</option>
                <option value={60}>1 hour before</option>
              </select>
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-gray-300">
                <input
                  type="checkbox"
                  checked={prefs.notifyOnBooking}
                  onChange={(e) => handlePrefsUpdate({ notifyOnBooking: e.target.checked })}
                  disabled={isUpdatingPrefs}
                  className="rounded bg-[#1a1a3a] border-[#2a2a4a]"
                />
                <span>Notify when a game is booked</span>
              </label>

              <label className="flex items-center gap-2 text-gray-300">
                <input
                  type="checkbox"
                  checked={prefs.notifyOnCancellation}
                  onChange={(e) => handlePrefsUpdate({ notifyOnCancellation: e.target.checked })}
                  disabled={isUpdatingPrefs}
                  className="rounded bg-[#1a1a3a] border-[#2a2a4a]"
                />
                <span>Notify when a game is cancelled</span>
              </label>

              <label className="flex items-center gap-2 text-gray-300">
                <input
                  type="checkbox"
                  checked={prefs.notifyOnLinkAccess}
                  onChange={(e) => handlePrefsUpdate({ notifyOnLinkAccess: e.target.checked })}
                  disabled={isUpdatingPrefs}
                  className="rounded bg-[#1a1a3a] border-[#2a2a4a]"
                />
                <span>Notify when someone opens your booking link</span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Calendar Export */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-4">Calendar Sync</h2>
        <p className="text-sm text-gray-400 mb-4">
          Sync your darts schedule with your favorite calendar app
        </p>

        {calendarInfo?.hasSubscription ? (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <a
                href={calendarInfo.googleCalendarUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center gap-2"
              >
                Add to Google Calendar
              </a>
              <a
                href={calendarInfo.appleCalendarUrl}
                className="btn-secondary inline-flex items-center gap-2"
              >
                Add to Apple Calendar
              </a>
              <button
                onClick={handleDownloadCalendar}
                className="btn-secondary"
              >
                Download .ics File
              </button>
            </div>

            <div className="text-xs text-gray-500">
              <p>Subscription URL:</p>
              <code className="block bg-[#1a1a3a] p-2 rounded mt-1 break-all">
                {calendarInfo.subscriptionUrl}
              </code>
            </div>

            <button
              onClick={handleGenerateCalendarToken}
              disabled={isGeneratingCalendar}
              className="text-sm text-gray-400 hover:text-white underline"
            >
              {isGeneratingCalendar ? 'Regenerating...' : 'Regenerate subscription link'}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              Create a subscription link to automatically sync your games with your calendar app.
            </p>
            <button
              onClick={handleGenerateCalendarToken}
              disabled={isGeneratingCalendar}
              className="btn-primary"
            >
              {isGeneratingCalendar ? 'Setting up...' : 'Set Up Calendar Sync'}
            </button>
            <button
              onClick={handleDownloadCalendar}
              className="btn-secondary ml-2"
            >
              Download .ics File
            </button>
          </div>
        )}
      </div>

      {/* Subscription */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-4">Subscription</h2>
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-gray-300">
              Current Plan: <span className="text-[#1111d4] capitalize">{user?.subscriptionTier || 'Free'}</span>
            </p>
            {user?.subscriptionTier === 'free' && (
              <p className="text-sm text-gray-500 mt-1">
                Upgrade to Pro for unlimited games and booking links
              </p>
            )}
          </div>
          {user?.subscriptionTier === 'free' && (
            <button className="btn-primary">
              Upgrade to Pro
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
